import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
// LexicalErrorBoundary import removed as we'll define our own
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { TextNode } from 'lexical';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { TRANSFORMERS } from '@lexical/markdown';
import { EditorState, LexicalEditor as LexicalEditorInstance, UNDO_COMMAND, REDO_COMMAND } from 'lexical';
import React, { useEffect, useRef, useState } from 'react'; // Added useState
import { debounce } from '../../components/Canvas/utils'; // Import debounce utility

import LexicalTheme from './LexicalTheme';
import FloatingToolbarPlugin from './FloatingToolbarPlugin';
import '../../styles/lexical-editor.css'; // Import the editor CSS styles
import '../../styles/lexical-toolbar.css'; // Import the toolbar CSS styles

// Placeholder for a real theme or styling
const editorStyle = {
  position: 'relative',
  background: 'transparent', // Ensure no background for the node itself
  outline: 'none', // Remove outline for the editor container
  height: '100%',
  width: '100%',
  resize: 'none', // Prevent manual resize of the contentEditable
  overflowY: 'auto', // Allow scrolling if content exceeds height
  color: 'inherit', // Inherit text color from parent (TextNode)
} as React.CSSProperties;

const placeholderStyle = {
  position: 'absolute',
  top: '0px', // Adjust as needed based on padding
  left: '0px', // Adjust as needed
  pointerEvents: 'none',
  userSelect: 'none',
  // Dark mode placeholder will be handled by a class or theme
  // Light mode color is now handled by className in Placeholder component
} as React.CSSProperties;


function Placeholder() {
  return <div style={placeholderStyle} className="text-gray-400 dark:text-gray-300">Enter text...</div>;
}

const editorConfig = {
  namespace: 'MrArchitectTextEditor',
  theme: LexicalTheme,
  onError(error: Error) {
    throw error;
  },
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode, // Though blockquotes are excluded, it's often a default part of RichText
    CodeNode,
    CodeHighlightNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
    TextNode, // Add TextNode to handle text formatting
  ],
  editable: true, // Will be controlled by TextNode's isEditing state
};

interface LexicalEditorProps {
  initialEditorState?: string | null; // Should always be a valid string from TextNode
  onChange: (editorState: EditorState, editor: LexicalEditorInstance, tags: Set<string>) => void;
  isEditing: boolean;
  onBlur?: (latestEditorState: string) => void; // Signature changed to pass latest state
  onRequestExitEditing?: () => void; // Callback to signal exiting edit mode
}

// Custom plugin to intercept keyboard events for undo/redo
const KeyboardCapturePlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  
  useEffect(() => {
    // This function will be called for every keydown event on the document
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle events when the editor has focus
      if (!editor.getRootElement()?.contains(event.target as Node)) {
        return;
      }
      
      const isUndoEvent = (event.metaKey || event.ctrlKey) && event.key === 'z' && !event.shiftKey;
      const isRedoEvent = (event.metaKey || event.ctrlKey) && event.key === 'z' && event.shiftKey;
      
      if (isUndoEvent || isRedoEvent) {
        // Prevent default browser behavior
        event.preventDefault();
        
        // Stop propagation to prevent the global handler from catching it
        event.stopPropagation();
        
        // Manually dispatch the appropriate command to Lexical
        if (isUndoEvent) {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        } else {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }
      }
    };
    
    // Add the event listener to the document with capture phase
    // This ensures we get the event before it reaches the editor
    document.addEventListener('keydown', handleKeyDown, true);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [editor]);
  
  return null;
};

// Component to focus the editor when isEditing becomes true
const AutoFocusPlugin: React.FC<{ isEditing: boolean }> = ({ isEditing }) => {
  const [editor] = useLexicalComposerContext();
  const wasEditingRef = useRef(false);
  
  useEffect(() => {
    // Only focus and move cursor when transitioning from non-editing to editing state
    if (isEditing && !wasEditingRef.current) {
      // Delay focus slightly to ensure the editor is fully rendered and interactive
      setTimeout(() => editor.focus(() => {
        // Move caret to the end of the content
        const root = editor.getRootElement();
        if (root) {
          const lastChild = root.lastChild;
          if (lastChild) {
            const selection = window.getSelection();
            if (selection) {
              const range = document.createRange();
              range.selectNodeContents(lastChild);
              range.collapse(false); // false to collapse to the end
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        }
      }), 0);
    }
    
    // Update ref to track editing state
    wasEditingRef.current = isEditing;
  }, [editor, isEditing]);
  
  return null;
};


export default function LexicalEditorComponent({
  initialEditorState,
  onChange: propsOnChange, // Renamed to avoid conflict in scope
  isEditing,
  onBlur: propsOnBlur, // Renamed to avoid conflict in scope
  onRequestExitEditing
}: LexicalEditorProps) {
  const lexicalEditorRef = useRef<LexicalEditorInstance | null>(null); // Ref to store editor instance
  const [lastSavedState, setLastSavedState] = useState<string | null>(initialEditorState || null);
  const [isFocused, setIsFocused] = useState(false); // State to track editor focus
  // const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Removed as it's unused
  
  // Create a debounced function to update the parent component
  // This helps reduce the frequency of store updates during rapid typing
  const debouncedUpdateParent = useRef(
    debounce((editorState: EditorState, _editor: LexicalEditorInstance) => { // Prefixed unused 'editor' param
      const serializedState = JSON.stringify(editorState.toJSON());
      
      // Only update if the content has actually changed
      if (serializedState !== lastSavedState) {
        setLastSavedState(serializedState);
        
        // This will trigger the TextNode to call updateNode on the store
        // which will in turn call takeSnapshot
        if (propsOnBlur) {
          propsOnBlur(serializedState);
        }
        
        // setHasUnsavedChanges(false); // Removed as hasUnsavedChanges state is removed
      }
    }, 500)
  ).current;

  const initialConfig = {
    ...editorConfig,
    editorState: initialEditorState, // TextNode ensures this is a valid string (empty or content)
    editable: isEditing,
  };

  // Use a key to force re-creation of the composer ONLY when isEditing changes from false to true,
  // ensuring the editor initializes correctly with editable state.
  // We no longer re-key on initialEditorState changes to prevent cursor reset issues
  const composerKey = `${isEditing}`;
  const editorShellRef = useRef<HTMLDivElement>(null); // Create ref for editor shell

// Custom Error Boundary
class MinimalErrorBoundary extends React.Component<{onError: (error: Error) => void; children: React.ReactElement;}, { hasError: boolean }> {
  constructor(props: {onError: (error: Error) => void; children: React.ReactElement;}) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _errorInfo: React.ErrorInfo) { // Prefixed unused 'errorInfo' param
    this.props.onError(error);
    // Log the error to an error reporting service
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      // For now, keeping it simple or just re-throwing to see if Lexical handles it
      return <div className="text-red-500">Error in editor.</div>;
    }
    return this.props.children;
  }
}

  return (
    <LexicalComposer initialConfig={initialConfig} key={composerKey}>
      <div ref={editorShellRef} className="editor-shell relative w-full h-full"> {/* Assign ref */}
        {/* Floating Toolbar will go here */}
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              style={{ ...editorStyle, userSelect: isEditing ? 'auto' : 'none' }}
              className="lexical-content-editable outline-none w-full h-full"
              onFocus={() => {
                setIsFocused(true);
              }}
              onBlur={() => {
                setIsFocused(false); // Update focus state for placeholder visibility

                // Capture props for use in timeout, as they might change
                const currentPropsOnBlur = propsOnBlur;
                const currentIsEditing = isEditing;
                const currentLastSavedState = lastSavedState;
                const currentSetLastSavedState = setLastSavedState;

                if (currentIsEditing && currentPropsOnBlur) {
                  setTimeout(() => {
                    // After a short delay, check if the editorShell is still mounted
                    // and if focus has genuinely moved away from the entire editor shell (including toolbar).
                    if (editorShellRef.current && // Use editorShellRef here
                        !editorShellRef.current.contains(document.activeElement)) {
                      
                      // Ensure lexicalEditorRef.current is also valid before accessing its state
                      if (lexicalEditorRef.current) { 
                        lexicalEditorRef.current.getEditorState().read(() => {
                        const editorState = lexicalEditorRef.current!.getEditorState();
                        const serializedState = JSON.stringify(editorState.toJSON());
                        
                        // The parent's onBlur (exitEditMode in TextNode) handles whether to save
                        // based on content change. We always call it to ensure exit.
                        if (serializedState !== currentLastSavedState) {
                          currentSetLastSavedState(serializedState);
                        }
                          currentPropsOnBlur(serializedState);
                        });
                      } else {
                        // Fallback or log if lexicalEditorRef is somehow not current
                        // This case should be rare if editorShellRef.current is valid
                        // and the component structure is as expected.
                        // Potentially, if only the lexical part unmounted but shell didn't.
                        console.warn("Lexical editor instance not available on blur, but shell was present.");
                        // Call propsOnBlur with last known good state or an empty state if necessary
                        // to ensure exitEditMode is still triggered in TextNode.
                        // If lexicalEditorRef.current is null, we can't get a new state.
                        // lexicalEditorRef.current is known to be null here.
                        // We must call propsOnBlur with a string.
                        if (currentLastSavedState !== null) {
                          currentPropsOnBlur(currentLastSavedState);
                        } else {
                          // This case implies initialEditorState was null and no changes occurred.
                          // Provide a string representation of an empty Lexical state.
                          const EMPTY_LEXICAL_STATE_STRING = JSON.stringify({"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}});
                          currentPropsOnBlur(EMPTY_LEXICAL_STATE_STRING);
                        }
                      }
                    }
                    // If focus returned to an element within the editor shell (e.g., toolbar button)
                    // within the timeout, this block is skipped, and edit mode is not exited.
                  }, 50); // 50ms delay, can be adjusted
                }
              }}
            />
          }
          placeholder={!isFocused ? <Placeholder /> : null}
          ErrorBoundary={MinimalErrorBoundary} 
        />
        <OnChangePlugin 
          onChange={(editorState, editor, tags) => {
            lexicalEditorRef.current = editor; // Store editor instance
            
            // Call the original onChange for any UI updates
            propsOnChange(editorState, editor, tags);
            
            // If we're in editing mode, track content changes for undo/redo
            if (isEditing) {
              // Check if this is a content change (not just selection)
              const isContentChange = !tags.has('historic') && !tags.has('selection');
              
              if (isContentChange) {
                // setHasUnsavedChanges(true); // Removed as hasUnsavedChanges state is removed
                
                // Use a timeout to ensure we don't interfere with cursor position
                // This allows the cursor position to be maintained during typing
                setTimeout(() => {
                  // Debounce updates to the parent to avoid too many snapshots
                  debouncedUpdateParent(editorState, editor);
                }, 0);
              }
            }
          }} 
          ignoreHistoryMergeTagChange={false} // We want to track history changes
          ignoreSelectionChange 
        />
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
        {isEditing && <KeyboardCapturePlugin />} {/* Add our custom keyboard event handler */}
        {isEditing && <AutoFocusPlugin isEditing={isEditing} />}
        {isEditing && <FloatingToolbarPlugin editorShellRef={editorShellRef} onExitEditingMode={onRequestExitEditing} />} {/* Pass ref and callback */}
      </div>
    </LexicalComposer>
  );
}
